import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, KeyRound, Tablet, X } from 'lucide-react'
import {
  deactivateDevice,
  fetchDeviceSetup,
  lastSeen,
  moveDevice,
  pinBlockedReason,
  pinProblem,
  registerDevice,
  setPin,
  type StudioDevice,
  type TeacherPin,
} from '../api/devices'

/**
 * Setting up the classroom tablets (D-116, D-120).
 *
 * One screen for one task, done once a season: put a tablet in each room, and
 * give the teachers a PIN. Splitting those across two screens would be filing
 * by data model rather than by what somebody is actually doing.
 *
 * **The setup code appears once.** It is stored hashed, like an invite (D-70),
 * so a lost one is replaced rather than recovered — which is also the moment
 * anybody notices a tablet has gone missing.
 */
export default function DeviceAdminView() {
  const queryClient = useQueryClient()
  const [issued, setIssued] = useState<{ name: string; setupCode: string } | null>(null)

  const { data, isPending, isError } = useQuery({
    queryKey: ['device-setup'],
    queryFn: fetchDeviceSetup,
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device-setup'] })

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Studio</p>
        <h1 className="font-display text-2xl">Classroom tablets</h1>
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          A tablet in each room shows the class that is on and lets the teacher take
          attendance with a PIN. Dancers can reach the music without unlocking anything.
        </p>
      </header>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {isError && <p className="text-sm text-crit">Could not load the tablets.</p>}

      {issued && <SetupCode issued={issued} onClose={() => setIssued(null)} />}

      {data && (
        <>
          <AddDevice
            rooms={data.rooms}
            onAdded={(result) => {
              setIssued(result)
              refresh()
            }}
          />

          <section className="mt-7">
            <h2 className="mb-2.5 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              Tablets
            </h2>

            {data.devices.length === 0 ? (
              <p className="rounded-2xl border border-line bg-surface px-5 py-6 text-center text-[13.5px] text-ink-3">
                No tablets yet. Add one above, then open{' '}
                <code className="rounded bg-surface-2 px-1.5 py-0.5">/room</code> on the tablet and
                enter its code.
              </p>
            ) : (
              <div className="flex flex-col gap-2.5">
                {data.devices.map((device) => (
                  <DeviceRow key={device.id} device={device} rooms={data.rooms} onChanged={refresh} />
                ))}
              </div>
            )}
          </section>

          <TeacherPins teachers={data.teachers} onChanged={refresh} />
        </>
      )}
    </div>
  )
}

function SetupCode({
  issued,
  onClose,
}: {
  issued: { name: string; setupCode: string }
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  return (
    <div className="mb-6 rounded-2xl border border-accent bg-accent-soft px-5 py-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-accent-2">
            Setup code for {issued.name}
          </p>

          {/* Said plainly, because there is no second chance and nobody reads
              the consequence after the fact. */}
          <p className="mt-1 text-[12.5px] text-ink-2">
            This is shown once and cannot be looked up again. Open{' '}
            <strong>/room</strong> on the tablet and enter it now.
          </p>

          <code className="mt-2.5 block rounded-xl border border-line-2 bg-surface px-3.5 py-2.5 text-[13px] break-all">
            {issued.setupCode}
          </code>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="rounded-lg p-1.5 text-ink-3 hover:text-ink"
        >
          <X size={17} strokeWidth={2} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(issued.setupCode)
          setCopied(true)
        }}
        className="mt-3 flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent"
      >
        {copied ? <Check size={13} strokeWidth={3} /> : <Copy size={13} strokeWidth={2} />}
        {copied ? 'Copied' : 'Copy code'}
      </button>
    </div>
  )
}

function AddDevice({
  rooms,
  onAdded,
}: {
  rooms: { id: string; name: string }[]
  onAdded: (result: { name: string; setupCode: string }) => void
}) {
  const [name, setName] = useState('')
  const [roomId, setRoomId] = useState('')

  const add = useMutation({
    mutationFn: () => registerDevice(name.trim(), roomId || null),
    onSuccess: (result) => {
      onAdded(result)
      setName('')
      setRoomId('')
    },
  })

  const field =
    'rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent'

  return (
    <section className="rounded-2xl border border-line bg-surface px-5 py-4">
      <h2 className="flex items-center gap-2 text-[14px] font-semibold">
        <Tablet size={16} strokeWidth={2} className="text-ink-3" />
        Add a tablet
      </h2>

      <div className="mt-3 flex flex-wrap items-end gap-2.5">
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-3">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Purple Room iPad"
            className={field}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-ink-3">Room</span>
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className={field}>
            {/* A tablet with no room still works — it just asks which class
                instead of assuming (D-116). */}
            <option value="">No fixed room</option>
            {rooms.map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          disabled={name.trim().length === 0 || add.isPending}
          onClick={() => add.mutate()}
          className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-40"
        >
          {add.isPending ? 'Adding...' : 'Add'}
        </button>
      </div>

      {add.isError && (
        <p role="alert" className="mt-2.5 text-[13px] text-crit">
          {(add.error as Error).message}
        </p>
      )}
    </section>
  )
}

function DeviceRow({
  device,
  rooms,
  onChanged,
}: {
  device: StudioDevice
  rooms: { id: string; name: string }[]
  onChanged: () => void
}) {
  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: onChanged,
  })

  return (
    <div
      className={`rounded-2xl border px-4 py-3.5 ${
        device.isActive ? 'border-line bg-surface' : 'border-line bg-surface-2 opacity-70'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] font-semibold">
            {device.name}
            {!device.isActive && (
              <span className="ml-2 rounded-md bg-surface-3 px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                Retired
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">{lastSeen(device.lastSeenAt)}</p>
        </div>

        {device.isActive && (
          <>
            <select
              value={device.roomId ?? ''}
              onChange={(e) => act.mutate(() => moveDevice(device.id, e.target.value || null))}
              aria-label={`Room for ${device.name}`}
              className="rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[13px] outline-none focus:border-accent"
            >
              <option value="">No fixed room</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => act.mutate(() => deactivateDevice(device.id))}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-crit"
            >
              Retire
            </button>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * PINs, beside the people they belong to.
 *
 * Set by the studio rather than chosen by the teacher: these are handed out at
 * the start of a season alongside a door code, and a self-service flow would
 * need email — which teachers reach far less reliably than they reach the
 * studio.
 */
function TeacherPins({ teachers, onChanged }: { teachers: TeacherPin[]; onChanged: () => void }) {
  const withPin = teachers.filter((t) => t.hasPin).length

  return (
    <section className="mt-8">
      <h2 className="mb-1 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
        Teacher PINs
      </h2>

      <p className="mb-3 text-[13px] text-ink-3">
        {withPin} of {teachers.length} can unlock a tablet. A PIN only takes attendance &mdash;
        it opens nothing else.
      </p>

      <div className="flex flex-col gap-2">
        {teachers.map((teacher) => (
          <TeacherRow key={teacher.staffId} teacher={teacher} onChanged={onChanged} />
        ))}
      </div>
    </section>
  )
}

function TeacherRow({ teacher, onChanged }: { teacher: TeacherPin; onChanged: () => void }) {
  const [editing, setEditing] = useState(false)
  const [pin, setPinValue] = useState('')

  const save = useMutation({
    mutationFn: (value: string | null) => setPin(teacher.staffId, value),
    onSuccess: () => {
      setEditing(false)
      setPinValue('')
      onChanged()
    },
  })

  const blocked = pinBlockedReason(teacher)
  const problem = pin.length > 0 ? pinProblem(pin) : null

  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold">{teacher.name}</p>

          {blocked ? (
            <p className="mt-0.5 text-[12.5px] text-warn">{blocked}</p>
          ) : (
            <p className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-ink-3">
              {teacher.hasPin && <KeyRound size={12} strokeWidth={2} />}
              {teacher.hasPin ? 'Has a PIN' : 'No PIN yet'}
            </p>
          )}
        </div>

        {!blocked && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              {teacher.hasPin ? 'Change' : 'Set a PIN'}
            </button>

            {teacher.hasPin && (
              <button
                type="button"
                onClick={() => save.mutate(null)}
                className="text-[12.5px] font-semibold text-ink-3 hover:text-crit"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <input
            value={pin}
            onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            autoFocus
            aria-label={`PIN for ${teacher.name}`}
            placeholder="4 or more digits"
            className="w-[10rem] rounded-lg border border-line-2 bg-surface px-3 py-1.5 text-[14px] tracking-[0.2em] outline-none focus:border-accent"
          />

          <button
            type="button"
            disabled={problem !== null || pin.length === 0 || save.isPending}
            onClick={() => save.mutate(pin)}
            className="rounded-lg bg-accent px-3.5 py-1.5 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
          >
            Save
          </button>

          <button
            type="button"
            onClick={() => {
              setEditing(false)
              setPinValue('')
            }}
            className="text-[12.5px] font-semibold text-ink-3"
          >
            Cancel
          </button>

          {problem && <span className="text-[12.5px] text-crit">{problem}</span>}
        </div>
      )}

      {save.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {(save.error as Error).message}
        </p>
      )}
    </div>
  )
}
